/**
 * adapters/cvVariantAdapter.js — RC 1.53 Phase 4.2
 *
 * Thin wrapper around `lib/cvVariantPolling.js::generateCvVariantWithPolling`.
 * The adapter exists so the section dispatches via the controller
 * (gaining the no-op-on-in-flight guard, lifecycle events, and the
 * `isAnyRunning` aggregate) without changing what the polling helper
 * does on the wire.
 *
 * Strict scope (Phase 4.2):
 *   • Wraps `generateCvVariantWithPolling` verbatim — same `requestBody`,
 *     same `apiPost`, same `apiGet`, same `onRunStatus`, same return
 *     shape.
 *   • Does NOT touch idempotency keys (the section mints them and
 *     stamps them onto requestBody.idempotency_key + apiPost body).
 *   • Does NOT call finalize-charge (the section owns finalize-charge).
 *   • Does NOT write URL params (the section owns URL state).
 *   • Does NOT refresh credits (the section calls credits.refresh()
 *     post-finalize-charge).
 *   • Does NOT invalidate listCache (Phase 4.3 work or the section
 *     consumer of `operation:complete` events).
 *   • Lazy-imports the polling helper so accidental top-level imports
 *     never bypass the controller's dispatch guard.
 *
 * Phase 4.3 will broaden this contract to take over post-success
 * side effects for the four-action consistency story; for now the
 * adapter is intentionally narrow.
 */

export const ACTION_CODE = 'cv_variant';

/**
 * run({ requestBody, apiPost, apiGet, onRunStatus, callbacks }) →
 *   Promise<{ variant_id, generated_text, ... }>
 *
 * `callbacks` is forwarded by the controller (onPolling, onProgress,
 * onRunIdReady) but Phase 4.2 only needs `onRunIdReady` to be
 * surfaced — the section already has its own onRunStatus → toast
 * pipeline that we preserve verbatim by forwarding onRunStatus
 * straight to the polling helper.
 */
export async function run({
  requestBody,
  apiPost,
  apiGet,
  onRunStatus,
  // CWS-CV (operator-locked, 2026-02-15) — forwarded straight into
  // the polling helper. Fires on async_202 with the resolved
  // variantId. Section uses it to patch the inflight marker before
  // any unmount window opens.
  onRunIdReady,
  callbacks,
  telemetryHeaders,
}) {
  if (!requestBody)   throw new Error('cvVariantAdapter.run: requestBody is required');
  if (typeof apiPost !== 'function') throw new Error('cvVariantAdapter.run: apiPost must be a function');
  if (typeof apiGet  !== 'function') throw new Error('cvVariantAdapter.run: apiGet must be a function');

  // Lazy import — guarantees that any caller bypassing the
  // controller's `dispatch` (e.g. a stray import in a section) would
  // hit the throw inside `dispatch` first, not import this module
  // and call `generateCvVariantWithPolling` directly.
  const { generateCvVariantWithPolling } = await import(
    /* webpackMode: "eager" */ '@/lib/cvVariantPolling.js'
  );

  // RC 1.53 Phase 4.2.1 — forward controller callbacks INTO the
  // polling helper's onRunStatus so the controller's operation:
  // progress event fires for every backend-side run_status
  // transition. The section's own `onRunStatus` (toast banners)
  // continues to run; this is additive only.
  const sectionOnRunStatus = onRunStatus;
  const wrappedOnRunStatus = (s) => {
    try {
      if (callbacks?.onProgress) callbacks.onProgress(s, s);
      if (s === 'running' && callbacks?.onPolling) callbacks.onPolling();
    } catch { /* noop — never let callback errors crash polling */ }
    if (typeof sectionOnRunStatus === 'function') {
      try { sectionOnRunStatus(s); } catch { /* noop */ }
    }
  };

  return generateCvVariantWithPolling({
    requestBody,
    apiPost,
    apiGet,
    onRunStatus: wrappedOnRunStatus,
    // CWS-CV (operator-locked, 2026-02-15) — pass the section's
    // onRunIdReady through so the marker can be upgraded BEFORE
    // polling resolves. Closes the audit's CV variant pre-runId
    // marker race.
    onRunIdReady,
    // RC 1.55 Phase Z.4 — forensic telemetry headers (READ-ONLY).
    // The section computes `X-Ndor-Job-Text-Source` from its own
    // resolution chain (snapshot | closure | refetch | none); the
    // adapter is a pure conduit. Bundle-version + dispatch-surface
    // are minted inside the polling helper itself.
    telemetryHeaders,
  });
}

export const PHASE_4_2_CONTRACT = Object.freeze({
  wrap_polling_helper: 'lib/cvVariantPolling.js::generateCvVariantWithPolling',
  url_param_contract:  ['source_cv_type=cv_variant', 'source_cv_id={id}'],
  inflight_marker_key: 'inflight-cv-variant:${userId}:${jobId}',
  side_effects_owned_by_adapter:  [],
  side_effects_owned_by_section: [
    'idempotency_key derivation (getOrMintIdempotencyKey)',
    'inflight marker write/clear (writeInflightCvMarker / clearInflightCvMarker)',
    'finalize-charge call',
    'URL param writes (source_cv_id, source_cv_type, last_variant_id)',
    'credits.refresh()',
    'cooldown start',
    'emit pack:artefact_ready (post-finalize-charge)',
  ],
});
